import { supabase } from '../lib/supabase';
import { addDays, addMonths } from 'date-fns';
import type { Switch, SwitchStep } from '../types';

export class SwitchService {
  private switchId: string;
  private steps: SwitchStep[];

  constructor(switchId: string) {
    this.switchId = switchId;
    this.steps = [];
  }

  private async updateSwitchStatus(status: Switch['status']) {
    const { error } = await supabase
      .from('switches')
      .update({ status })
      .eq('id', this.switchId);

    if (error) throw error;
  }

  private async updateStepStatus(stepId: string, status: SwitchStep['status']) {
    const { error } = await supabase
      .from('switch_steps')
      .update({
        status,
        completed_at: status === 'completed' ? new Date().toISOString() : null
      })
      .eq('id', stepId);

    if (error) throw error;
  }

  private async createNotification(title: string, message: string, type: 'switch') {
    const { error } = await supabase
      .from('notifications')
      .insert({
        title,
        message,
        type
      });

    if (error) throw error;
  }

  async processSwitch() {
    try {
      // Get switch details
      const { data: switchData, error: switchError } = await supabase
        .from('switches')
        .select(`
          *,
          previous_service (id, name, provider, contract_end_date, auto_renewal),
          new_deal (id, provider, service_category, cost)
        `)
        .eq('id', this.switchId)
        .single();

      if (switchError) throw switchError;

      // Get switch steps
      const { data: steps, error: stepsError } = await supabase
        .from('switch_steps')
        .select('*')
        .eq('switch_id', this.switchId)
        .order('created_at', { ascending: true });

      if (stepsError) throw stepsError;
      this.steps = steps;

      // Process each step
      for (const step of this.steps) {
        // Simulate processing time
        await new Promise(resolve => setTimeout(resolve, 2000));

        try {
          switch (step.description) {
            case 'Verifying current service details':
              await this.verifyServiceDetails(switchData);
              break;
            case 'Initiating new service setup':
              await this.initiateNewService(switchData);
              break;
            case 'Scheduling service transition':
              await this.scheduleTransition(switchData);
              break;
            case 'Canceling old service':
              await this.cancelOldService(switchData);
              break;
          }

          await this.updateStepStatus(step.id, 'completed');
          await this.createNotification(
            'Switch Progress',
            `Step completed: ${step.description}`,
            'switch'
          );

        } catch (error) {
          await this.updateStepStatus(step.id, 'failed');
          await this.updateSwitchStatus('failed');
          await this.createNotification(
            'Switch Failed',
            `Failed at step: ${step.description}. Please contact support.`,
            'switch'
          );
          throw error;
        }
      }

      // Update switch status to completed
      await this.updateSwitchStatus('completed');
      await this.createNotification(
        'Switch Completed',
        'Your service switch has been completed successfully!',
        'switch'
      );

      // Update service status
      const { error: updateError } = await supabase
        .from('services')
        .update({ status: 'inactive' })
        .eq('id', switchData.previous_service.id);

      if (updateError) throw updateError;

      // Create new service
      const { error: newServiceError } = await supabase
        .from('services')
        .insert({
          name: `${switchData.new_deal.service_category} - ${switchData.new_deal.provider}`,
          provider: switchData.new_deal.provider,
          category: switchData.new_deal.service_category,
          cost: switchData.new_deal.cost,
          contract_end_date: addMonths(new Date(), 12).toISOString(),
          auto_renewal: false,
          no_commitment: false,
          status: 'active'
        });

      if (newServiceError) throw newServiceError;

    } catch (error) {
      console.error('Switch processing failed:', error);
      throw error;
    }
  }

  private async verifyServiceDetails(switchData: any) {
    const service = switchData.previous_service;
    
    // Check if service is eligible for switching
    if (!service) throw new Error('Service not found');
    
    // Check contract end date if applicable
    if (!service.no_commitment && service.contract_end_date) {
      const endDate = new Date(service.contract_end_date);
      if (endDate > new Date()) {
        // If auto-renewal is enabled, we can still proceed
        if (!service.auto_renewal) {
          throw new Error('Service is still under contract');
        }
      }
    }
  }

  private async initiateNewService(switchData: any) {
    // Simulate API call to new provider's system
    await new Promise(resolve => setTimeout(resolve, 2000));
  }

  private async scheduleTransition(switchData: any) {
    // Calculate transition date (usually 2 weeks from now)
    const transitionDate = addDays(new Date(), 14);
    
    // Update switch record with transition date
    const { error } = await supabase
      .from('switches')
      .update({ transition_date: transitionDate.toISOString() })
      .eq('id', this.switchId);

    if (error) throw error;
  }

  private async cancelOldService(switchData: any) {
    const service = switchData.previous_service;
    
    // Simulate API call to current provider's system
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Update service status
    const { error } = await supabase
      .from('services')
      .update({ 
        status: 'cancellation_pending',
        cancellation_date: addDays(new Date(), 14).toISOString()
      })
      .eq('id', service.id);

    if (error) throw error;
  }
}